<template>
  <div ref="root" class="contents" @focusout="leave" @keydown.esc.stop.prevent="expanded = false">
    <input
      :value="modelValue"
      :disabled="disabled"
      type="text"
      role="combobox"
      aria-label="Rechercher une référence"
      aria-autocomplete="list"
      :aria-expanded="showResults"
      :aria-controls="showResults && !loading && matches.length ? listId : undefined"
      :aria-activedescendant="active >= 0 && showResults ? `${listId}-${active}` : undefined"
      autocomplete="off"
      placeholder="Rechercher une référence…"
      class="col-start-1 row-start-1 w-full min-w-0 rounded border px-3 py-2"
      maxlength="100"
      @input="typeReference"
      @focus="expanded = true"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="enter"
    />
    <div v-if="showResults" class="col-span-2 row-start-2 overflow-hidden rounded-lg border border-indigo-200 bg-white shadow-lg">
      <p v-if="loading" class="p-3 text-sm text-gray-600" role="status">Recherche des articles…</p>
      <p v-else-if="!matches.length" class="p-3 text-sm text-gray-600" role="status">Aucun article trouvé. Essayez une autre référence.</p>
      <div v-else :id="listId" role="listbox" aria-label="Résultats des références" class="divide-y divide-gray-100">
        <button
          v-for="(item, index) in matches"
          :id="`${listId}-${index}`"
          :key="item.id_produit"
          type="button"
          role="option"
          :aria-selected="active === index"
          :aria-label="`Sélectionner ${item.reference} · ${item.couleur} · ${totalLabel(item)}`"
          :class="['flex w-full items-center gap-3 p-3 text-left hover:bg-indigo-50 focus:bg-indigo-50', active === index ? 'bg-indigo-50' : 'bg-white']"
          @mousedown.prevent
          @click="select(item.id_produit)"
        >
          <span class="flex h-16 w-14 shrink-0 items-center justify-center rounded bg-gray-50">
            <img v-if="item.image_url && !failedImages.has(item.image_url)" :src="item.image_url" :alt="item.reference" class="h-full w-full object-contain" referrerpolicy="no-referrer" @error="failedImages.add(item.image_url)" />
            <span v-else class="px-1 text-center text-xs text-gray-500">Sans photo</span>
          </span>
          <span class="min-w-0 text-sm">
            <span class="block break-words font-semibold">{{ item.reference }}</span>
            <span class="block break-words text-gray-600">{{ item.couleur || 'Couleur non renseignée' }}</span>
            <span class="block font-medium text-indigo-700">{{ totalLabel(item) }}</span>
            <span class="block text-xs text-gray-600">{{ replacementProductTotal(item, selection).description }}</span>
          </span>
        </button>
      </div>
      <p v-if="!loading && products.length > matches.length" class="border-t bg-gray-50 px-3 py-2 text-xs text-gray-600">{{ matches.length }} premiers résultats sur {{ products.length }}. Précisez la référence ou consultez les articles ci-dessous.</p>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { replacementMoney, replacementProductTotal } from '@/api/remplacements';
import type { ReplacementProduct, ReplacementSelection } from '@/api/remplacements';

const props = defineProps<{ modelValue: string; products: ReplacementProduct[]; selection?: ReplacementSelection; loading: boolean; disabled?: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [value: string]; choose: [id: number]; search: [] }>();
const root = ref<HTMLElement>();
const expanded = ref(false);
const active = ref(-1);
const failedImages = ref(new Set<string>());
const listId = `replacement-reference-${getCurrentInstance()?.uid}`;
const matches = computed(() => props.products.slice(0, 5));
const showResults = computed(() => expanded.value && Boolean(props.modelValue.trim()) && !props.disabled);
function totalLabel(item: ReplacementProduct) {
  const total = replacementProductTotal(item, props.selection).amount;
  return total != null ? `${replacementMoney(total)} HT au total` : 'Quantité à préciser';
}
function typeReference(event: Event) {
  expanded.value = true;
  emit('update:modelValue', (event.target as HTMLInputElement).value);
}
function select(id: number) {
  emit('choose', id);
  expanded.value = false;
  active.value = -1;
}
function move(direction: number) {
  expanded.value = true;
  if (props.loading || !matches.value.length) return;
  active.value = direction > 0 ? (active.value + 1) % matches.value.length : active.value < 0 ? matches.value.length - 1 : (active.value - 1 + matches.value.length) % matches.value.length;
}
function enter() {
  const item = matches.value[active.value];
  if (showResults.value && !props.loading && item) select(item.id_produit);
  else emit('search');
}
function leave(event: FocusEvent) {
  if (!root.value?.contains(event.relatedTarget as Node | null)) expanded.value = false;
}
watch(() => [props.modelValue, props.products], () => { active.value = -1; });
</script>
